import React, { useEffect, useState } from 'react';
import { Heart, X, Check, AlertCircle } from 'lucide-react';
import { getInvitePreview, acceptInvite, declineInvite } from '../lib/invites.js';
import { signInWithGoogle, signInWithFacebook } from '../lib/auth.js';
import { DARK_THEME } from '../lib/theme.js';

export function AcceptInvitePage({ user, inviteCode, onAccepted }) {
  const theme = DARK_THEME;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getInvitePreview(inviteCode).then(p => {
      setPreview(p);
      setLoading(false);
    }).catch(e => {
      console.error('Preview failed:', e);
      setError('Kunne ikke hente invitation');
      setLoading(false);
    });
  }, [inviteCode]);

  async function handleAccept() {
    if (!user) {
      // Save invite code so we redirect back here after login
      localStorage.setItem('tt_pending_invite', inviteCode);
      try {
        await signInWithGoogle();
      } catch (e) {
        setError('Kunne ikke starte login');
      }
      return;
    }

    setSubmitting(true);
    try {
      const result = await acceptInvite(inviteCode);
      if (result.success) {
        localStorage.removeItem('tt_pending_invite');
        onAccepted();
      } else {
        setError(translateError(result.error));
        setSubmitting(false);
      }
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    if (!user) {
      // Just navigate away — no need to log in just to decline
      window.location.href = '/';
      return;
    }
    setSubmitting(true);
    try {
      await declineInvite(inviteCode);
      window.location.href = '/';
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  async function handleLoginGoogle() {
    localStorage.setItem('tt_pending_invite', inviteCode);
    try {
      await signInWithGoogle();
    } catch (e) {
      setError('Kunne ikke starte Google login');
    }
  }

  async function handleLoginFacebook() {
    localStorage.setItem('tt_pending_invite', inviteCode);
    try {
      await signInWithFacebook();
    } catch (e) {
      setError('Kunne ikke starte Facebook login');
    }
  }

  if (loading) {
    return <FullScreen theme={theme}><div style={{ color: theme.textMuted }}>Henter invitation...</div></FullScreen>;
  }

  if (!preview?.found) {
    return <FullScreen theme={theme}>
      <ErrorCard theme={theme} icon={AlertCircle} title="Invitation findes ikke" body="Tjek at linket er korrekt — eller bed din partner sende et nyt." />
    </FullScreen>;
  }

  if (preview.status === 'expired') {
    return <FullScreen theme={theme}>
      <ErrorCard theme={theme} icon={AlertCircle} title="Invitationen er udløbet" body="Bed din partner sende en ny invitation." />
    </FullScreen>;
  }

  if (preview.status === 'accepted') {
    return <FullScreen theme={theme}>
      <ErrorCard theme={theme} icon={Check} title="Allerede accepteret" body="Denne invitation er allerede blevet accepteret." />
    </FullScreen>;
  }

  if (preview.status === 'declined' || preview.status === 'cancelled') {
    return <FullScreen theme={theme}>
      <ErrorCard theme={theme} icon={X} title="Invitationen er ikke længere aktiv" body="Bed din partner sende en ny invitation." />
    </FullScreen>;
  }

  // Status = pending — show the accept UI
  return (
    <FullScreen theme={theme}>
      <div style={{
        maxWidth: 380, width: '100%',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <svg width="48" height="48" viewBox="0 0 120 120" style={{ display: 'block', margin: '0 auto 12px' }}>
            <g transform="translate(60,60)">
              <rect x="-30" y="-20" width="20" height="40" rx="3" fill={theme.coral}/>
              <rect x="-36" y="-20" width="32" height="9" rx="3" fill={theme.coral}/>
              <rect x="6" y="-22" width="20" height="40" rx="3" fill={theme.gold}/>
              <rect x="0" y="-22" width="32" height="9" rx="3" fill={theme.gold}/>
            </g>
          </svg>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Tag Team</div>
        </div>

        {/* Inviter card */}
        <div style={{
          background: theme.bgElevated,
          border: `1px solid ${theme.border}`,
          borderRadius: 16,
          padding: 24,
          textAlign: 'center',
          marginBottom: 16,
        }}>
          {preview.inviter_photo ? (
            <img src={preview.inviter_photo} alt="" style={{
              width: 72, height: 72,
              borderRadius: '50%',
              objectFit: 'cover',
              margin: '0 auto 12px',
              display: 'block',
              border: `3px solid ${theme.coral}`,
            }} />
          ) : (
            <div style={{
              width: 72, height: 72,
              background: theme.coral,
              borderRadius: '50%',
              color: 'white',
              fontSize: 30, fontWeight: 800,
              margin: '0 auto 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>{(preview.inviter_name || '?').charAt(0)}</div>
          )}

          <div style={{ fontSize: 14, color: theme.textMuted, marginBottom: 4 }}>
            <Heart size={14} color={theme.coral} fill={theme.coral} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Du er inviteret af
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{preview.inviter_name}</h1>
          <p style={{ margin: '12px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
            til at være partnere på Tag Team. I kan dele opgaver, indkøb og høste kærlighedspoint sammen.
          </p>
        </div>

        {/* Action area */}
        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#F87171',
            fontSize: 12,
            borderRadius: 10,
            marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        {!user ? (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: theme.textMuted, textAlign: 'center' }}>
              Log ind for at acceptere
            </p>
            <button onClick={handleLoginGoogle} style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: `1px solid ${theme.border}`,
              background: theme.bgElevated,
              color: theme.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8,
            }}>
              <GoogleIcon /> Fortsæt med Google
            </button>
            <button onClick={handleLoginFacebook} style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              background: '#1877F2',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}>
              <FacebookIcon /> Fortsæt med Facebook
            </button>
          </>
        ) : (
          <>
            <button onClick={handleAccept} disabled={submitting} style={{
              width: '100%',
              padding: '14px 16px',
              background: theme.coral,
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginBottom: 8,
            }}>
              <Heart size={14} /> {submitting ? 'Accepterer...' : 'Acceptér og bliv partner'}
            </button>
            <button onClick={handleDecline} disabled={submitting} style={{
              width: '100%',
              padding: '12px 16px',
              background: 'transparent',
              color: theme.textMuted,
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
              Afvis invitation
            </button>
          </>
        )}
      </div>
    </FullScreen>
  );
}

function FullScreen({ theme, children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>{children}</div>
  );
}

function ErrorCard({ theme, icon: Icon, title, body }) {
  return (
    <div style={{
      maxWidth: 380, width: '100%',
      background: theme.bgElevated,
      border: `1px solid ${theme.border}`,
      borderRadius: 16,
      padding: 24,
      textAlign: 'center',
    }}>
      <Icon size={36} color={theme.textMuted} style={{ margin: '0 auto 12px' }} />
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h2>
      <p style={{ margin: '8px 0 16px', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>{body}</p>
      <button onClick={() => window.location.href = '/'} style={{
        background: theme.coral,
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}>Gå til Tag Team</button>
    </div>
  );
}

function translateError(code) {
  switch (code) {
    case 'invite_not_found': return 'Invitationen findes ikke';
    case 'invite_expired': return 'Invitationen er udløbet';
    case 'invite_already_processed': return 'Invitationen er allerede behandlet';
    case 'cannot_accept_own_invite': return 'Du kan ikke acceptere din egen invitation';
    case 'inviter_already_paired': return 'Inviteren har allerede en partner';
    case 'invitee_already_paired': return 'Du har allerede en partner. Afslut det nuværende parforhold først.';
    case 'not_authenticated': return 'Du skal være logget ind';
    default: return 'Der opstod en fejl: ' + code;
  }
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669c1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}
